import React, { useEffect } from 'react';
import { css } from '@emotion/css';
import Button from '../../../../../components/general/Button';
import ConfirmationDialog from '../../../../../components/structure/ConfirmationDialog';
import WarningConfirmation from '../../../../../components/general/WarningConfirmation';
import { color_shades_dark, color_variants_shadow_10, dark_grey_blue_70 } from '../../../../../constants/colors';
import ApplicationModel from '../../../../../models/applications/api/ApplicationModel';
import StyledDropdown from '../../../../applicationSettings/components/customAssistantConfigurations/StyledDropdown';
import styled from '@emotion/styled';
import { breakpoint_small } from '../../../../../constants/breakpoints';
import { getSparkMainBaseUrl } from '../../../../../constants/Urls';
import ZohoInstructionsModal from './ZohoInstructionsModal';
import { getZohoConnectedApplications } from '../../../../../api/controllers/businessManagement';
import ConnectedAppResponse from '../../../../../models/integrations/api/Salesforce/ConnectedAppResponse';

const infoIcon = require('../../../../../content/images/info_icon.svg');

interface ZohoCRMIntegrationProps {
    applications: ApplicationModel[];
    isLoading: boolean;
    organizationId: string;
}

const ZohoCRMIntegration: React.FC<ZohoCRMIntegrationProps> = ({ applications, isLoading, organizationId }) => {
    const [showZohoModal, setShowZohoModal] = React.useState(false);
    const [showInstructionsModal, setShowInstructionsModal] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState('');
    const [connectedApps, setConnectedApps] = React.useState<ConnectedAppResponse[]>([]);
    const [isLoadingConnectedApps, setIsLoadingConnectedApps] = React.useState(false);
    const [zohoCredentials, setZohoCredentials] = React.useState<{ clientId: string, clientSecret: string }>({
        clientId: '',
        clientSecret: ''
    });

    useEffect(() => {
        const fetchConnectedApps = async () => {
            if (!organizationId || !applications.length) return;

            setIsLoadingConnectedApps(true);
            try {
                const applicationIds = applications
                    .map(app => app.id)
                    .filter(id => id && id.trim());

                const result = await getZohoConnectedApplications(organizationId, applicationIds);

                if (result.resultType === "Ok" && result.data) {
                    setConnectedApps(result.data);
                }
            } catch (error) {
                console.error('Error fetching connected applications:', error);
            }
            setIsLoadingConnectedApps(false);
        };

        fetchConnectedApps();
    }, [organizationId, applications]);

    const getRedirectUri = () => {
        return `${getSparkMainBaseUrl()}/v/orgSettings`;
    }

    const handleZohoConnect = () => {
        const redirectUri = getRedirectUri();
        const clientId = zohoCredentials.clientId;
        const scope = "ZohoCRM.modules.ALL,ZohoCRM.coql.READ";
        window.location.href = `https://accounts.zoho.com/oauth/v2/auth?response_type=code&client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&access_type=offline&state=ZohoCRM-${selectedLocation}-id-${clientId}-secret-${zohoCredentials.clientSecret}`;
    };

    const isAppConnected = (appId: string) => {
        return connectedApps.some(connectedApp => connectedApp.applicationId === appId);
    };

    const locationOptions = applications.map(app => ({
        value: app.id,
        label: isAppConnected(app.id) ? `${app.name} - ACCOUNT CONNECTED` : app.name
    }));

    return (
        <>
            <div className={integrationCardStyle}>
                <div className={integrationCardContentStyle}>
                    <div className={headerStyle}>
                        <h2>Zoho CRM Integration</h2>
                        <Button
                            themes={['icon']}
                            icon={infoIcon}
                            onClick={() => setShowInstructionsModal(true)}
                            className={infoButtonStyle}
                        />
                    </div>
                    <p>Zoho CRM is a cloud-based customer relationship management platform that helps businesses manage sales, marketing, and customer support.</p>
                    <p>Connect to Zoho CRM to give your assistant access to client and customer information.</p>
                    <div className={dropdownContainerStyle}>
                        <CustomDropdown
                            disabled={isLoading || isLoadingConnectedApps}
                            handleOnChange={(_, value) => setSelectedLocation(value as string)}
                            options={locationOptions}
                            value={selectedLocation}
                            placeholder={isLoadingConnectedApps ? "Loading connected apps..." : "Select a location"}
                            size="medium"
                        />
                    </div>
                    <div className={credentialsContainerStyle}>
                        <input
                            type="text"
                            placeholder="Client ID"
                            value={zohoCredentials.clientId}
                            onChange={(e) => setZohoCredentials(prev => ({ ...prev, clientId: e.target.value }))}
                            className={inputStyle}
                        />
                        <input
                            type="password"
                            placeholder="Client Secret"
                            value={zohoCredentials.clientSecret}
                            onChange={(e) => setZohoCredentials(prev => ({ ...prev, clientSecret: e.target.value }))}
                            className={inputStyle}
                        />
                    </div>
                </div>
                <Button
                    themes={["primary"]}
                    text="Authenticate"
                    className={authenticateButtonStyle(selectedLocation === '' || zohoCredentials.clientId === '' || zohoCredentials.clientSecret === '')}
                    onClick={() => selectedLocation && zohoCredentials.clientId && zohoCredentials.clientSecret && setShowZohoModal(true)}
                />
            </div>
            {showZohoModal && (
                <ConfirmationDialog
                    title="Connect to Zoho CRM"
                    onClose={() => setShowZohoModal(false)}
                    onConfirm={handleZohoConnect}
                    deleteText="Authenticate"
                >
                    <WarningConfirmation
                        showImage={false}
                        text="By authenticating to Zoho CRM, you grant access to your client and customer information. This integration helps provide better service through voice interactions."
                    />
                </ConfirmationDialog>
            )}
            {showInstructionsModal && (
                <ZohoInstructionsModal onClose={() => setShowInstructionsModal(false)} />
            )}
        </>
    );
};

const integrationCardStyle = css`
    border: 1px solid ${color_shades_dark};
    display: flex;
    margin-top: 10px;
    justify-content: space-between;
    border-radius: 8px;
    padding: 24px;
    background: white;
    box-shadow: 0 2px 4px 0 ${color_variants_shadow_10};
    h2 {
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: 600;
    }
    
    p {
        margin-bottom: 6px;
        font-size: 14px;
        color: ${dark_grey_blue_70};
    }

    ${breakpoint_small} {
        margin-top: 5px;
        display: block;
    }
`;

const integrationCardContentStyle = css`
    width: 50%;
    ${breakpoint_small} {
        width: 100%;
    }
`;

const dropdownContainerStyle = css`
    margin-top: 16px;
`;

const credentialsContainerStyle = css`
    padding: 4px;
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const inputStyle = css`
    padding: 8px 12px;
    border: 1px solid ${color_shades_dark};
    border-radius: 4px;
    font-size: 14px;
    width: 100%;

    &:focus {
        outline: none;
        border-color: #0066cc;
    }
`;

const authenticateButtonStyle = (disabled: boolean) => css`
    align-self: center;
    ${disabled && css`
        opacity: 0.5;
        cursor: not-allowed;
    `}
`;

const CustomDropdown = styled(StyledDropdown)`
    padding: 5px;
    margin: 0 0 24px 0;
    
    .Select-menu-outer {
        max-height: none !important;
        z-index: 10001;
        padding: 10px 0px;
    }
    
    .Select-menu {
        max-height: 300px;
    }
`;

const headerStyle = css`
    display: flex;
    align-items: center;
    gap: 0px;
    margin-bottom: 16px;
`;

const infoButtonStyle = css`
    padding: 0;
    margin-top: 4px;
    img {
        width: 20px;
        height: 20px;
    }
`;

export default ZohoCRMIntegration; 